import React, {useEffect, useState, useMemo, useRef} from "react";
import ReactModal from "react-modal";
import { useForm, FormProvider, get } from "react-hook-form";
import { Draggable} from "@components";
import { Input, Radio } from "@components/form";
import { useLectureUserModal } from "@hooks";
import { post } from "@utils/axios_api";

function loadNicepayScript() {
  return new Promise((resolve, reject) => {
    if (window.goPay) return resolve();
    const existed = document.querySelector('script[src="https://pg-web.nicepay.co.kr/v3/common/js/nicepay-pgweb.js"]');
    if (existed) {
      existed.addEventListener('load', () => resolve());
      existed.addEventListener('error', () => reject(new Error('NICEPAY SDK load failed')));
      return;
    }
    const s = document.createElement("script");
    s.src = "https://pg-web.nicepay.co.kr/v3/common/js/nicepay-pgweb.js";
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("NICEPAY SDK load failed"));
    document.head.appendChild(s);
  });
}

const fmtBiz = (v) => {
    const d = v.replace(/\D/g, "").slice(0, 11);
    return d.length < 4 ? d
            : d.length < 6 ? `${d.slice(0,3)}-${d.slice(3)}`
                        : `${d.slice(0,3)}-${d.slice(3,5)}-${d.slice(5)}`;
};
const ModalUser = ({ isOpen, onClose, lecture, onOpenConfirm }) => {

    const position = Draggable({ isOpen, dragHandle: ".modal-header" });

    const BANK_INFO = {
        bank: "한국산업은행",
        account: "022-2600-4645-704",
        holder: "(주식회사) 아토스",
    };

    const containerStyle = {
      width: "900px",
    maxWidth: "900px",
    minWidth: "900px",
    height: "90vh",                  // ✅ 뷰포트 기준 높이
    display: "flex",                 // ✅ 세로 배치
    flexDirection: "column",
    background: "#fff",
    borderRadius: "8px",
    overflow: "hidden",              // ✅ 내부만 스크롤하게
    // 드래그 좌표 적용 (기존 유지)
    transform: `translate(${position.x}px, ${position.y}px)`
      };

    const { userInfo, applyMutation, searchBiz, } = useLectureUserModal(isOpen);

    const mem = useMemo(() => userInfo?.result ?? {}, [userInfo?.result]);

    const initBiz = useMemo(() => ({
        bizRegNo     : mem.bizRegNo     || "",
        companyNm    : mem.companyNm    || "",
        representNm  : mem.representNm  || "",
        taxMgrNm     : mem.taxMgrNm     || "",
        taxMgrEmail  : mem.taxMgrEmail  || "",
        taxPhone     : mem.taxPhone  || "",
        zipcode      : mem.zipcode  || "",
        address1     : mem.address1  || "",
        address2     : mem.address2  || "",
        bizTypeNm    : mem.bizTypeNm  || "",
        bizItemNm    : mem.bizItemNm  || "",
        payType      : "A",
        depositName  : mem.depositName || "",
    }), [mem]);

    const methods = useForm({ defaultValues: initBiz });
    const { setValue, reset, getValues, handleSubmit, watch } = methods;
    const payType = watch("payType");
    const [edit, setEdit] = useState(!initBiz.bizRegNo);

    useEffect(() => {
        if (isOpen) {
          reset(initBiz);
          setEdit(!initBiz.bizRegNo);
        }
      }, [isOpen, initBiz, reset]);

    const onSearch = async () => {
        const no = getValues("bizRegNo");
        if (!no) return alert("사업자번호를 입력 하세요.");
        const {result} = await searchBiz(no);
        if (result) {
          Object.entries(result).forEach(([k,v]) => setValue(k, v || ""));
          setEdit(false);
        } else {
          alert("신규 사업자입니다. 새로 입력 해주세요.");
          setEdit(true);
        }
    };

    const handleAddressSearch = () => {
        new window.daum.Postcode({
        oncomplete: function (data) {
            setValue("zipcode", data.zonecode, { shouldValidate: true });
            setValue("address1", data.address, { shouldValidate: true });
        },
        }).open();
    };

    const onError = () => {
        alert("필수 입력란을 확인하세요.");
    }

    // ✅ 히든 결제폼 참조
  const payFormRef = useRef(null);

  // ✅ 전역 콜백 바인딩 (이름 변경 금지)
  useEffect(() => {
    // 결제창에서 인증 완료 후 호출됨: 폼을 서버로 submit
    window.nicepaySubmit = function () {
      try {
        // 서버의 인증응답 처리 URL로 submit (아래 백엔드 2) 참고)
        if (payFormRef.current) payFormRef.current.submit();
      } catch (e) {
        alert("결제 응답 처리 중 오류가 발생했습니다.");
      }
    };
    // 결제창 닫힘 콜백
    window.nicepayClose = function () {
      alert("결제가 취소되었습니다.");
    };
    return () => {
      delete window.nicepaySubmit;
      delete window.nicepayClose;
    };
  }, []);

  // ✅ 결제창 호출 함수
  const openNicepay = async (orderReady) => {
    // orderReady: 서버에서 받은 서명/필수 파라미터 묶음
    await loadNicepayScript(); // goPay 로드
    const form = payFormRef.current;
    if (!form || !window.goPay) {
      alert("결제 모듈 로드에 실패했습니다."); 
      return;
    }
    // 서버에서 내려준 값 채우기 (매뉴얼 필드명 준수) :contentReference[oaicite:2]{index=2}
    // 필수: GoodsName, Amt, MID, EdiDate, Moid, SignData, PayMethod
    // 선택: BuyerName, BuyerTel, BuyerEmail, CharSet 등
    const assign = (name, val) => {
      let input = form.querySelector(`input[name="${name}"]`);
      if (!input) {
        input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        form.appendChild(input);
      }
      input.value = val ?? "";
    };

    const {
      goodsName, amt, mid, ediDate, moid, signData, payMethod,
      buyerName, buyerTel, buyerEmail, charSet
    } = orderReady;

    assign("GoodsName", goodsName);
    assign("Amt", amt);
    assign("MID", mid);
    assign("EdiDate", ediDate);
    assign("Moid", moid);
    assign("SignData", signData);
    assign("PayMethod", payMethod || "CARD");
    if (buyerName)  assign("BuyerName",  buyerName);
    if (buyerTel)   assign("BuyerTel",   buyerTel);
    if (buyerEmail) assign("BuyerEmail", buyerEmail);
    assign("CharSet", charSet || "utf-8");

    // goPay 호출 → 레이어 결제창 오픈 (PC) :contentReference[oaicite:3]{index=3}
    window.goPay(form);
  };

    const onSubmit = async (formData) => {
        if (payType === "A" && !formData.depositName) {
            alert("입금자명을 입력해 주세요.");
            return;
        }
        if (!window.confirm("교육 신청 하시겠습니까?")) return;

        if(payType === 'B'){
            alert("일반결제입니다");
            //await get();
        }

        if (payType === 'C') {
        try {
            const orderId = `${lecture?.lectureId || 'LECT'}-${Date.now()}`; // 유니크 키
      // 백엔드: /api/pay/nicepay/ready (아래 2) 참고)
      const readyRes = await post("/pay/nicepay/ready", {
        lectureId: lecture.lectureId,
        moid: orderId,
        amount: 1004,  //lecture?.eduPee
        buyerName: mem.memberNm,
        buyerTel: mem.phoneNo,
        buyerEmail: mem.email,
        payMethod: "CARD",
        goodsName: lecture?.lectureNm || "교육비"
        });

      if (readyRes?.ok !== true) {
        alert(readyRes?.message || "결제 사전준비(서명) 실패");
        return;
      }

      // 4) 히든폼 action=서버 인증응답 처리 URL
      //    nicepaySubmit()에서 이 폼을 submit → 서버가 승인 API 호출
      const BASE_URL = process.env.REACT_APP_BOOT_URL;
      if (payFormRef.current) {
        payFormRef.current.action = `${BASE_URL}/api/pay/nicepay/auth/callback`;
        payFormRef.current.method = "POST";
        // CharSet이 euc-kr 기본이지만, 우리는 utf-8로 받도록 서버/매뉴얼 설정 반영
        payFormRef.current.acceptCharset = "euc-kr"; // 매뉴얼 권고 (응답은 CharSet 옵션으로 UTF-8 가능) :contentReference[oaicite:4]{index=4}
      }

      await openNicepay(readyRes.data);   // goPay 호출 → 결제창 오픈
    } catch (err) {
      console.error(err);
      alert("결제창 호출에 실패했습니다.");
    }
        // 카드결제는 결제창 → (POST returnUrl) → 서버 승인 후 UI 갱신 플로우.
        // 여기서 바로 신청 API를 호출하지 않습니다. (서버에서 성공 처리 후 상태 반영)
        return;
      }
        const payload = {
          lectureId: lecture.lectureId,
          ...formData
        };

        try {
            const {message} = await applyMutation.mutateAsync(payload);
            alert(message);
            onClose();
            onOpenConfirm();

        } catch (error) {
            alert(error.message || '신청 중 오류가 발생했습니다.');
          }
    }


    return (
    <ReactModal
        isOpen={isOpen}
        onRequestClose={onClose}
        overlayClassName="modal-overlay"
        className="modal-wrap"
    >
        <div id="modal-container"  style={containerStyle}>
            <div className="modal-header"  style={{ cursor: "move", flex: "0 0 auto" }}>
                <h3>교육신청 정보확인</h3>
                <button type="button" className="modal-btn-close" onClick={onClose}>
                    <span className="modal-btn-span">X</span>
                </button>
            </div>
            <div className="modal-content" style={{ flex: "1 1 auto", overflowY: "auto", padding: "16px" }}>
            <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit, onError)}> 
                <span className="modal_span">교육 정보</span>
                <div className="lineTop_tb01">
                    <table className="lineTop_tb2 table-td-left">
                    <colgroup>
                        <col style={{ width: '170px' }} />
                        <col />
                        <col style={{ width: '170px' }} />
                        <col />
                    </colgroup>
                    <tbody>
                        <tr>
                        <th>교육명</th>
                        <td>{lecture?.lectureNm || ''}</td>
                        <th>교육일자</th>
                        <td>{lecture.firstDt && lecture.lastDt ? `${lecture.firstDt} ~ ${lecture.lastDt}` : lecture.firstDt || "-"} ({lecture?.eduHours}시간)</td>
                        </tr>
                        <tr>
                        <th>교육비</th>
                        <td colSpan={3}>{lecture.eduPee ? `₩ ${lecture.eduPee.toLocaleString()} ` : "-"}</td>
                        </tr>
                    </tbody>
                    </table>
                </div>
                <span className="modal_span">개인정보</span>
                <div className="lineTop_tb01">
                    <table className="lineTop_tb2 table-td-left">
                    <colgroup>
                        <col style={{ width: '170px' }} />
                        <col />
                        <col style={{ width: '170px' }} />
                        <col />
                    </colgroup>
                    <tbody>
                        <tr>
                        <th>아이디</th>
                        <td>{mem.memberId || ''}</td>
                        <th>이름</th>
                        <td>{mem.memberNm || ''}</td>
                        </tr>
                        <tr>
                        <th>전화번호</th>
                        <td>{mem.phoneNo || ''}</td>
                        <th>이메일</th>
                        <td>{mem.email || ''}</td>
                        </tr>
                    </tbody>
                    </table>
                </div>
                <span className="modal_span">사업자정보</span>
                <div className="lineTop_tb01">
                    <table className="lineTop_tb2 table-td-left point">
                    <colgroup>
                        <col style={{ width: '170px' }} />
                        <col  />
                        <col style={{ width: '170px' }} />
                        <col />
                    </colgroup>
                    <tbody>
                        <tr>
                            <th>사업자등록번호 <b>*</b></th>
                            <td colSpan={3}>
                            <div style={{display:"flex", gap:"10px"}}>
                            <Input 
                                name="bizRegNo" readOnly={!edit} className="inp-sel-sub"
                                style={{width:"240px"}}
                                onChange={e=>setValue('bizRegNo', fmtBiz(e.target.value))}
                                rules={{ required:true }}
                            />
                                {edit ? 
                                    <>
                                        <button type="button" className="btn btn-blue" onClick={onSearch}>조회</button>
                                    </>
                                    : <button type="button" className="btn btn-blue" 
                                        onClick={()=> { setEdit(true); reset(initBiz); }}>수정</button>}
                            </div>
                            </td>
                            
                        </tr>
                        <tr>
                            <th>사업장명 <b>*</b></th>
                            <td ><Input name="companyNm"   className="inp-sel-sub" rules={{ required:true }}/></td>
                            <th>대표자명 <b>*</b></th>
                            <td ><Input name="representNm"   className="inp-sel-sub" rules={{ required:true }}/></td>
                        </tr>
                        <tr>
                            <th>업태 <b>*</b></th>
                            <td >
                                <Input
                                    name="bizTypeNm"
                                    placeholder="업태"
                                    className="inp-sel-sub"
                                    rules={{ required:true }}
                                />
                            </td>
                            <th>종목 <b>*</b></th>
                            <td >
                                <Input
                                    name="bizItemNm"
                                    placeholder="종목"
                                    className="inp-sel-sub"
                                    rules={{ required:true }}
                                />
                            </td>
                        </tr>
                        <tr>
                            <th>세금계산서 담당자 <b>*</b></th>
                            <td><Input name="taxMgrNm"   className="inp-sel-sub" rules={{ required:true }}/></td>
                            <th>담당자 연락처 <b>*</b></th>
                            <td>
                                <Input
                                    name="taxPhone"
                                    placeholder="ex) 02-123-4567"
                                    className="inp-sel-sub"
                                    rules={{
                                    validate: (v) =>
                                        !v || v === "" || /^\d{2,3}-\d{3,4}-\d{4}$/.test(v) ||
                                        "예) 02-1234-5678 형식으로 입력",
                                    }}
                                    onInput={e => (e.target.value = e.target.value.replace(/[^0-9-]/g, ""))}
                                />
                                </td>
                        </tr>
                        <tr>    
                            <th>담당자 이메일 <b>*</b></th>
                            <td colSpan={3} >
                                <p className="mt5" style={{ color: "#888" }}>
                                ※ 아래의 이메일로 계산서가 발행됩니다 (무통장, 현장결제만 발행)
                                </p>
                                <Input name="taxMgrEmail"   className="inp-sel-sub" rules={{ required:true }}/></td>
                        </tr>
                        <tr>
                          <th>주소 <b>*</b></th>
                          <td colSpan={3} >
                            <div className="fom-item mb5">
                              <Input
                                name="zipcode"
                                placeholder="우편번호"
                                readOnly
                                className="inp-sel-sub mr5"
                                style={{width:"370px"}}
                              />
                              <button
                                type="button"
                                className="btn btn-blue ml5"
                                onClick={handleAddressSearch}
                                
                              >
                                주소검색
                              </button>
                            </div>
                              <Input
                                name="address1"
                                placeholder="주소"
                                readOnly
                                className="inp-sel-sub mr5 mt10"
                                maxLength={30}
                                style={{width:"470px"}}
                              />
                              <Input
                                name="address2"
                                placeholder="상세주소를 입력하세요"
                                className="inp-sel-sub mt10"
                                maxLength={30}
                                style={{width:"470px"}}
                              />
                          </td>

                        </tr>
                        <tr>    
                            <th>결제 방법 <b>*</b></th>
                            <td colSpan={3}>
                                <div style={{display:"flex", gap:"15px"}}>
                                    <Radio name="payType" label="무통장 입금" value="A" />
                                    <Radio name="payType" label="현장 결제" value="B" />
                                    <Radio name="payType" label="카드 결제" value="C" />
                                </div>
                            </td>
                        </tr>
                        {/* ▼ 무통장 입금일 때만 노출 */}
                        {payType === "A" && (
                        <>
                            <tr>
                            <th>입금 계좌</th>
                            <td colSpan={3}>
                                <div className="fom-item">
                                <input
                                    className="inp-sel-sub"
                                    readOnly
                                    value={`${BANK_INFO.bank} ${BANK_INFO.account} (${BANK_INFO.holder})`}
                                    style={{ width: "60%" }}
                                />
                                <button
                                    type="button"
                                    className="btn btn-blue ml10"
                                    onClick={() => {
                                    navigator.clipboard?.writeText(`${BANK_INFO.bank} ${BANK_INFO.account} ${BANK_INFO.holder}`);
                                    alert("계좌 정보를 복사했습니다.");
                                    }}
                                >
                                    계좌복사
                                </button>
                                </div>
                                <p className="mt5" style={{ color: "#888" }}>
                                * 교육비 입금 후 확인까지 시간이 소요될 수 있습니다.
                                </p>
                            </td>
                            </tr>
                            <tr>
                            <th>입금자명 <b>*</b></th>
                            <td colSpan={3}>
                                <Input
                                name="depositName"
                                placeholder="입금자명을 입력하세요"
                                className="inp-sel-sub"
                                style={{ width: "40%" }}
                                // 숨길 때 RHF에서 필드 제거 → 다른 결제수단일 때 검증 충돌 방지
                                shouldUnregister={true}
                                rules={{ required: true, maxLength: 50 }}
                                />
                            </td>
                            </tr>
                        </>
                        )}
                    </tbody>
                    </table>
                </div>
                <div className="text-cent">
                    <button type="button" className="btn btn-blue mt18" onClick={onClose}>닫기</button>
                    <button type="submit" className="btn btn_red mt18  ml10" >결제하기</button>
                </div>
                <form name="payForm" ref={payFormRef} style={{display:"none"}} />
            </form>      
            </FormProvider>
            </div>
        </div>
    </ReactModal>
    );
}

export {ModalUser}